import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { theme } from '../../../theme';
import { useResponsive } from '../../../hooks/useResponsive';
import AuthModal from '../../../components/AuthModal';
import UpgradeToOwnerModal from '../../../components/modals/UpgradeToOwnerModal';
import { Search, Calendar, CheckCircle, BarChart2, Clock, CreditCard, ArrowRight, ChevronDown } from 'lucide-react';

// ─── Design tokens (aligned with Futbolero Style Guide) ─────────────────────
const C = {
  bg: '#181A1B',
  surface: '#23272A',
  surfaceAlt: '#1E2224',
  border: '#33383B',
  primary: '#00E676',
  primaryDark: '#1DE9B6',
  danger: '#FF1744',
  warning: '#FFD600',
  text: '#FFFFFF',
  textSecondary: '#B0BEC5',
  textMuted: '#6B7280',
} as const;

const F = {
  display: "'Plus Jakarta Sans', system-ui, sans-serif",
  body: "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif",
} as const;

// ─── Types ───────────────────────────────────────────────────────────────────
interface DemoFormState {
  name: string;
  email: string;
  complexName: string;
  city: string;
  phone: string;
}

// ─── Sub-components ──────────────────────────────────────────────────────────
const Logo: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    <div style={{
      width: size + 8,
      height: size + 8,
      background: C.primary,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transform: 'rotate(-5deg)',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: size * 0.65 }}>⚽</span>
    </div>
    <span style={{
      fontSize: size,
      fontWeight: 700,
      color: C.primary,
      fontFamily: F.display,
      letterSpacing: '1px',
    }}>
      FUTBOLERO
    </span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const OwnerLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, selectRole } = useAuth();
  const { isMobile } = useResponsive();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Demo form state
  const [demoForm, setDemoForm] = useState<DemoFormState>({ name: '', email: '', complexName: '', city: '', phone: '' });
  const [demoStatus, setDemoStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [demoError, setDemoError] = useState('');

  // Player search
  const [searchCity, setSearchCity] = useState('');

  const handleOwnerCTA = () => {
    if (!user) {
      setShowLoginModal(true);
    } else if (user.role === 'VENUE_OWNER' || user.role === 'BOTH') {
      navigate('/owner/onboarding');
    } else {
      setShowUpgradeModal(true);
    }
  };

  const handleUpgradeToOwner = async () => {
    try {
      await selectRole('BOTH');
      setShowUpgradeModal(false);
      navigate('/owner/onboarding');
    } catch {
      alert('Error al habilitar funciones de propietario.');
    }
  };

  const scrollToDemo = () => {
    document.getElementById('solicitar-demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email || !demoForm.complexName || !demoForm.city) {
      setDemoError('Completá todos los campos obligatorios.');
      return;
    }
    setDemoStatus('loading');
    setDemoError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/demo-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoForm),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al enviar');
      }
      setDemoStatus('success');
    } catch (err: any) {
      setDemoError(err.message || 'Error al enviar. Intentá de nuevo.');
      setDemoStatus('error');
    }
  };

  // ─── Shared styles ──────────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: '8px',
    color: C.text,
    fontSize: '15px',
    fontFamily: F.body,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const ctaPrimaryStyle: React.CSSProperties = {
    padding: isMobile ? '14px 24px' : '16px 32px',
    backgroundColor: C.primary,
    color: '#0A0A0A',
    border: 'none',
    borderRadius: '12px',
    fontSize: isMobile ? '15px' : '16px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: F.body,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    height: '52px',
  };

  const ctaSecondaryStyle: React.CSSProperties = {
    ...ctaPrimaryStyle,
    backgroundColor: 'transparent',
    color: C.text,
    border: `1.5px solid ${C.border}`,
  };

  return (
    <div style={{ backgroundColor: C.bg, minHeight: '100vh', color: C.text, fontFamily: F.body }}>

      {/* ═══════════════════════════════════════════════════════
          HEADER
      ═══════════════════════════════════════════════════════ */}
      <header style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 100,
        backgroundColor: `${C.bg}F0`,
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.border}`,
        padding: isMobile ? '0 20px' : '0 48px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Logo size={isMobile ? 22 : 26} />

        <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '24px' }}>
          {!isMobile && (
            <button
              onClick={() => navigate('/venues')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: C.textSecondary, fontSize: '14px', fontFamily: F.body,
                fontWeight: 500,
              }}
            >
              Para Jugadores ↗
            </button>
          )}
          <button
            data-testid="header-solicitar-demo"
            onClick={scrollToDemo}
            style={{
              padding: isMobile ? '8px 16px' : '10px 22px',
              backgroundColor: C.primary,
              color: '#0A0A0A',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: F.body,
            }}
          >
            {isMobile ? 'Demo' : 'Solicitar Demo'}
          </button>
          <button
            onClick={() => setShowLoginModal(true)}
            style={{
              background: 'none', border: `1px solid ${C.border}`,
              color: C.textSecondary, fontSize: '14px',
              fontFamily: F.body, cursor: 'pointer',
              padding: '8px 16px', borderRadius: '8px',
            }}
          >
            Iniciar sesión
          </button>
        </nav>
      </header>

      {/* Spacer */}
      <div style={{ height: '64px' }} />

      {/* ═══════════════════════════════════════════════════════
          HERO — DUAL AUDIENCE
      ═══════════════════════════════════════════════════════ */}
      <section style={{
        minHeight: isMobile ? 'auto' : '88vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: isMobile ? '56px 20px 48px' : '80px 80px 64px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background gradient */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse at 70% 50%, ${C.primary}08 0%, transparent 60%)`,
          pointerEvents: 'none',
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <div style={{ maxWidth: isMobile ? '100%' : '660px' }}>

            {/* Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              backgroundColor: `${C.primary}15`,
              border: `1px solid ${C.primary}40`,
              borderRadius: '20px',
              padding: '6px 16px',
              marginBottom: '24px',
            }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: C.primary, display: 'inline-block' }} />
              <span style={{ fontSize: '13px', color: C.primary, fontWeight: 600, fontFamily: F.body }}>
                Plataforma deportiva · Buenos Aires
              </span>
            </div>

            {/* H1 */}
            <h1 style={{
              fontSize: isMobile ? '52px' : '80px',
              fontWeight: 700,
              color: C.text,
              margin: '0 0 20px',
              fontFamily: F.display,
              letterSpacing: '1px',
              lineHeight: 1.05,
            }}>
              TU CANCHA.<br />
              <span style={{ color: C.primary }}>MÁS TURNOS.</span><br />
              CERO PAPELES.
            </h1>

            <p style={{
              fontSize: isMobile ? '16px' : '18px',
              color: C.textSecondary,
              margin: '0 0 36px',
              lineHeight: 1.65,
              maxWidth: '520px',
            }}>
              Reservas automáticas 24/7, grilla diaria, turnos fijos y caja en un solo lugar.
              Sin WhatsApp, sin llamadas perdidas. <strong style={{ color: C.text }}>100% gratis durante la beta.</strong>
            </p>

            {/* CTAs */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '48px' }}>
              <button
                style={ctaPrimaryStyle}
                onClick={() => navigate('/demo')}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#00D96F')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = C.primary)}
              >
                <ArrowRight size={18} />
                Ver Demo en Vivo
              </button>
              <button
                style={ctaSecondaryStyle}
                onClick={scrollToDemo}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.text; }}
              >
                Solicitar acceso
                <ChevronDown size={16} />
              </button>
            </div>

            {/* Secondary: player link */}
            <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 48px' }}>
              ¿Buscás una cancha para jugar?{' '}
              <button
                data-testid="cta-buscar-canchas"
                onClick={() => navigate('/venues')}
                style={{ background: 'none', border: 'none', color: C.textSecondary, cursor: 'pointer', fontSize: '13px', fontFamily: F.body, textDecoration: 'underline', padding: 0 }}
              >
                Encontrá tu cancha acá
              </button>
            </p>

            {/* Social proof */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '-6px' }}>
                {['🟢', '🟢', '🟢'].map((_, i) => (
                  <div key={i} style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    backgroundColor: C.surface, border: `2px solid ${C.bg}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginLeft: i > 0 ? '-8px' : 0, fontSize: '14px',
                  }}>⚽</div>
                ))}
              </div>
              <span style={{ fontSize: '13px', color: C.textMuted }}>
                +12 complejos en Buenos Aires ya usando Futbolero
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          HOW IT WORKS — DUEÑOS
      ═══════════════════════════════════════════════════════ */}
      <section style={{
        backgroundColor: C.surfaceAlt,
        borderTop: `1px solid ${C.border}`,
        borderBottom: `1px solid ${C.border}`,
        padding: isMobile ? '48px 20px' : '64px 80px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '28px' : '36px',
            fontFamily: F.display,
            color: C.text,
            margin: '0 0 8px',
            textAlign: 'center',
          }}>
            EMPEZÁ EN 3 PASOS
          </h2>
          <p style={{ color: C.textSecondary, textAlign: 'center', margin: '0 0 40px', fontSize: '15px' }}>
            Sin técnicos, sin contratos, sin complicaciones
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '20px',
          }}>
            {[
              { icon: <CheckCircle size={28} color={C.primary} />, num: '01', title: 'Registrá tu complejo', desc: 'Cargá tus canchas, horarios y precios en menos de 10 minutos.' },
              { icon: <Calendar size={28} color={C.primary} />, num: '02', title: 'Recibís reservas', desc: 'Los jugadores encuentran tu cancha y reservan solos, 24/7.' },
              { icon: <BarChart2 size={28} color={C.primary} />, num: '03', title: 'Gestionás desde el panel', desc: 'Grilla diaria, turnos fijos, caja y reportes en un solo lugar.' },
            ].map((step, i) => (
              <div key={i} style={{
                backgroundColor: C.surface,
                borderRadius: '12px',
                padding: '28px',
                border: `1px solid ${C.border}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  {step.icon}
                  <span style={{
                    fontSize: '42px',
                    fontFamily: F.display,
                    color: `${C.primary}20`,
                    lineHeight: 1,
                    fontWeight: 700,
                  }}>{step.num}</span>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 600, color: C.text }}>{step.title}</div>
                <div style={{ fontSize: '14px', color: C.textSecondary, lineHeight: 1.6 }}>{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          OWNER SECTION + SOLICITAR DEMO
      ═══════════════════════════════════════════════════════ */}
      <section id="solicitar-demo" style={{
        padding: isMobile ? '60px 20px' : '96px 80px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '56px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              backgroundColor: `${C.primary}15`,
              border: `1px solid ${C.primary}40`,
              borderRadius: '20px',
              padding: '6px 16px',
              marginBottom: '20px',
            }}>
              <span style={{ fontSize: '13px', color: C.primary, fontWeight: 600 }}>⚽ Para dueños de canchas</span>
            </div>

            <h2 style={{
              fontSize: isMobile ? '36px' : '56px',
              fontFamily: F.display,
              color: C.text,
              margin: '0 0 16px',
              letterSpacing: '0.5px',
              lineHeight: 1.1,
            }}>
              ¿SOS DUEÑO DE CANCHA?<br />
              <span style={{ color: C.primary }}>SUMÁ TU COMPLEJO.</span>
            </h2>

            <p style={{
              fontSize: isMobile ? '15px' : '17px',
              color: C.textSecondary,
              maxWidth: '580px',
              margin: '0 auto',
              lineHeight: 1.65,
            }}>
              Gestioná reservas, turnos fijos y pagos desde una sola plataforma.
              Sin comisiones. Sin contratos. 100% gratis durante la beta.
            </p>
          </div>

          {/* Benefits + Form side by side */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '40px' : '64px',
            alignItems: 'start',
          }}>

            {/* Left: Benefits */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  icon: <Clock size={24} color={C.primary} />,
                  title: 'Reservas automáticas 24/7',
                  desc: 'Tus clientes reservan online mientras vos dormís. Sin llamadas perdidas.',
                },
                {
                  icon: <CreditCard size={24} color={C.primary} />,
                  title: 'Registro de caja automático',
                  desc: 'Cada reserva queda registrada. Al final del día sabés exactamente cuánto entraste.',
                },
                {
                  icon: <BarChart2 size={24} color={C.primary} />,
                  title: 'Dashboard profesional',
                  desc: 'Grilla de reservas diaria/semanal, turnos fijos, analytics y reportes.',
                },
                {
                  icon: <CheckCircle size={24} color={C.primary} />,
                  title: '100% gratis en la beta',
                  desc: 'Sin comisiones por reserva. Los primeros complejos entran gratis para siempre.',
                },
              ].map((b, i) => (
                <div key={i} style={{
                  display: 'flex',
                  gap: '16px',
                  backgroundColor: C.surface,
                  borderRadius: '12px',
                  padding: '20px',
                  border: `1px solid ${C.border}`,
                }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '10px',
                    backgroundColor: `${C.primary}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {b.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: C.text, marginBottom: '4px' }}>{b.title}</div>
                    <div style={{ fontSize: '13px', color: C.textSecondary, lineHeight: 1.6 }}>{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Demo Form */}
            <div>
              <div style={{
                backgroundColor: C.surface,
                borderRadius: '18px',
                padding: isMobile ? '28px 20px' : '36px',
                border: `1px solid ${C.border}`,
                position: 'sticky',
                top: '80px',
              }}>
                {demoStatus === 'success' ? (
                  <div data-testid="demo-form-success" style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ fontSize: '56px', marginBottom: '16px' }}>🎉</div>
                    <h3 style={{ fontFamily: F.display, fontSize: '28px', color: C.primary, margin: '0 0 12px' }}>
                      ¡SOLICITUD ENVIADA!
                    </h3>
                    <p style={{ color: C.textSecondary, fontSize: '15px', lineHeight: 1.6, margin: '0 0 20px' }}>
                      Te enviamos un email con las credenciales de acceso al demo.<br />
                      Revisá tu bandeja de entrada.
                    </p>
                    <p style={{ color: C.textMuted, fontSize: '13px' }}>
                      Nuestro equipo te contactará pronto.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 style={{
                      fontFamily: F.display,
                      fontSize: '26px',
                      color: C.text,
                      margin: '0 0 6px',
                    }}>
                      SOLICITAR ACCESO
                    </h3>
                    <p style={{ color: C.textSecondary, fontSize: '13px', margin: '0 0 16px', lineHeight: 1.6 }}>
                      Completá el formulario y te contactamos en menos de 24hs.
                    </p>

                    {/* Demo CTA */}
                    <button
                      onClick={() => navigate('/demo')}
                      style={{
                        width: '100%',
                        padding: '13px',
                        backgroundColor: `${C.primary}20`,
                        border: `1.5px solid ${C.primary}60`,
                        borderRadius: '10px',
                        color: C.primary,
                        fontWeight: 700,
                        fontSize: '14px',
                        fontFamily: F.body,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        marginBottom: '20px',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = `${C.primary}30`; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = `${C.primary}20`; }}
                    >
                      <ArrowRight size={16} />
                      Ver el panel en vivo (sin registrarte)
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                      <div style={{ flex: 1, height: '1px', background: C.border }} />
                      <span style={{ color: C.textMuted, fontSize: '12px' }}>o dejá tus datos</span>
                      <div style={{ flex: 1, height: '1px', background: C.border }} />
                    </div>

                    <form data-testid="demo-form" onSubmit={handleDemoSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                          Tu nombre *
                        </label>
                        <input
                          data-testid="demo-form-name"
                          style={inputStyle}
                          type="text"
                          placeholder="Juan García"
                          value={demoForm.name}
                          onChange={e => setDemoForm(f => ({ ...f, name: e.target.value }))}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                          Email *
                        </label>
                        <input
                          data-testid="demo-form-email"
                          style={inputStyle}
                          type="email"
                          placeholder="juan@complejo.com"
                          value={demoForm.email}
                          onChange={e => setDemoForm(f => ({ ...f, email: e.target.value }))}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                          Nombre del complejo *
                        </label>
                        <input
                          data-testid="demo-form-complex"
                          style={inputStyle}
                          type="text"
                          placeholder="Complejo Deportivo San Martín"
                          value={demoForm.complexName}
                          onChange={e => setDemoForm(f => ({ ...f, complexName: e.target.value }))}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                            Ciudad *
                          </label>
                          <input
                            data-testid="demo-form-city"
                            style={inputStyle}
                            type="text"
                            placeholder="Buenos Aires"
                            value={demoForm.city}
                            onChange={e => setDemoForm(f => ({ ...f, city: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: C.textSecondary, marginBottom: '6px' }}>
                            Teléfono
                          </label>
                          <input
                            data-testid="demo-form-phone"
                            style={inputStyle}
                            type="tel"
                            placeholder="+5491112345678"
                            value={demoForm.phone}
                            onChange={e => setDemoForm(f => ({ ...f, phone: e.target.value }))}
                          />
                        </div>
                      </div>

                      {demoError && (
                        <p data-testid="demo-form-error" style={{ color: C.danger, fontSize: '13px', margin: '0' }}>{demoError}</p>
                      )}

                      <button
                        data-testid="demo-form-submit"
                        type="submit"
                        disabled={demoStatus === 'loading'}
                        style={{
                          ...ctaPrimaryStyle,
                          width: '100%',
                          justifyContent: 'center',
                          opacity: demoStatus === 'loading' ? 0.6 : 1,
                          marginTop: '4px',
                        }}
                        onMouseEnter={e => { if (demoStatus !== 'loading') e.currentTarget.style.backgroundColor = '#00D96F'; }}
                        onMouseLeave={e => { if (demoStatus !== 'loading') e.currentTarget.style.backgroundColor = C.primary; }}
                      >
                        {demoStatus === 'loading' ? 'Enviando...' : (
                          <>Solicitar acceso gratis <ArrowRight size={18} /></>
                        )}
                      </button>

                      <p style={{ fontSize: '12px', color: C.textMuted, textAlign: 'center', margin: '0' }}>
                        Sin tarjeta de crédito · Sin compromiso
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          ROI — SIMPLE
      ═══════════════════════════════════════════════════════ */}
      <section style={{
        backgroundColor: C.surfaceAlt,
        borderTop: `1px solid ${C.border}`,
        padding: isMobile ? '48px 20px' : '64px 80px',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: F.display, fontSize: isMobile ? '28px' : '40px', color: C.text, margin: '0 0 8px' }}>
            ¿CUÁNTO PODÉS GANAR MÁS POR MES?
          </h2>
          <p style={{ color: C.textSecondary, margin: '0 0 36px', fontSize: '15px' }}>
            Estimación conservadora para 1 cancha de fútbol 5
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
            marginBottom: '32px',
          }}>
            {[
              { label: 'Reservas recuperadas', value: '$90.000', desc: '5 turnos nuevos/mes que antes perdías por no estar online' },
              { label: 'Turnos fijos asegurados', value: '$54.000', desc: '3 clientes recurrentes que antes llamaban y se olvidaban' },
              { label: 'Clientes del marketplace', value: '$36.000', desc: '2 grupos nuevos/mes que te encuentran en Futbolero' },
            ].map((item, i) => (
              <div key={i} style={{
                backgroundColor: C.surface,
                borderRadius: '12px',
                padding: '24px',
                border: `1px solid ${C.border}`,
                textAlign: 'left',
              }}>
                <div style={{ fontSize: '28px', fontWeight: 700, color: C.primary, fontFamily: F.display, marginBottom: '6px' }}>
                  {item.value}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '4px' }}>{item.label}</div>
                <div style={{ fontSize: '13px', color: C.textSecondary, lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            ))}
          </div>

          <div style={{
            backgroundColor: `${C.primary}10`,
            border: `1px solid ${C.primary}30`,
            borderRadius: '12px',
            padding: '20px 32px',
            display: 'inline-flex',
            gap: '16px',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <span style={{ color: C.textSecondary, fontSize: '16px' }}>Ganancia mensual extra estimada:</span>
            <span style={{ fontFamily: F.display, fontSize: '40px', color: C.primary, fontWeight: 700 }}>$180.000</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════════════════ */}
      <footer style={{
        backgroundColor: C.bg,
        borderTop: `1px solid ${C.border}`,
        padding: isMobile ? '32px 20px' : '48px 80px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <Logo size={20} />

          <div style={{ display: 'flex', gap: '24px', fontSize: '13px', color: C.textSecondary }}>
            <a href="/" style={{ color: C.textSecondary, textDecoration: 'none' }}>Para Jugadores</a>
            <a href="/privacy" style={{ color: C.textSecondary, textDecoration: 'none' }}>Privacidad</a>
          </div>

          <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>
            © 2026 Futbolero. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modals */}
      <AuthModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <UpgradeToOwnerModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgradeToOwner}
      />
    </div>
  );
};

export default OwnerLandingPage;
